import { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const formatData = (v) => (v ? String(v).substring(0, 16).replace("T", " ") : "");

export default function Inventarios() {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const [loading, setLoading] = useState(false);

  const [centros, setCentros] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [inventarios, setInventarios] = useState([]);

  const [inventarioAberto, setInventarioAberto] = useState(null);
  const [materiaisInventario, setMateriaisInventario] = useState(null);

  const [centro, setCentro] = useState(null);
  const [fabricante, setFabricante] = useState(null);
  const [marca, setMarca] = useState(null);
  const [incluirEncerrados, setIncluirEncerrados] = useState(false);

  const idutilizador = Number(userData?.id || 0);

  const carregarCentros = async () => {
    const [data, err] = await handleApi(api.post("Stocks/SelInventarioCentrosCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setCentros(
      Array.isArray(data)
        ? data.map((x) => ({
            ...x,
            refcentro: String(x.refcentro || "").trim(),
            centro: String(x.centro || x.refcentro || "").trim(),
          }))
        : []
    );
  };

  const carregarFabricantes = async () => {
    const [data, err] = await handleApi(api.post("Fabricantes/SelFabricantesCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setFabricantes(Array.isArray(data) ? data : []);
  };

  const carregarMarcas = async (reffabricante) => {
    setMarcas([]);
    setMarca(null);

    if (!reffabricante) return;

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarioMarcasCombo", { reffabricante, term: "" })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    setMarcas(Array.isArray(data) ? data : []);
  };

  const carregarInventarios = async () => {
    if (!idutilizador) return;

    setLoading(true);

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarios", {
        idutilizador,
        incluirEncerrados: incluirEncerrados ? 1 : 0,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setInventarios(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!idutilizador) return;

    setLoading(true);

    Promise.all([carregarCentros(), carregarFabricantes(), carregarInventarios()]).finally(() =>
      setLoading(false)
    );
  }, [idutilizador]);

  useEffect(() => {
    carregarInventarios();
  }, [incluirEncerrados]);

  const criarInventario = async () => {
    if (!centro?.refcentro) {
      showAlert("Seleciona o centro.", "warning");
      return;
    }

    if (!fabricante?.ref) {
      showAlert("Seleciona o fabricante.", "warning");
      return;
    }

    if (!marca?.ref) {
      showAlert("Seleciona a marca.", "warning");
      return;
    }

    setLoading(true);

    const [, err, meta] = await handleApi(
      api.post("Stocks/InsInventario", {
        refcentro: centro.refcentro,
        reffabricante: fabricante.ref,
        refmarca: marca.ref,
        idutilizadorabertura: idutilizador,
        obs: "",
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Inventário criado.", "success");
    await carregarInventarios();
  };

  const abrirInventario = async (row) => {
    setInventarioAberto(row);
    setLoading(true);

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarioMateriais", {
        idinventario: row.id,
        idutilizador,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setMateriaisInventario(data || null);
  };

  const inventariosFiltrados = useMemo(() => {
    return inventarios.filter((x) => {
      if (centro?.refcentro && String(x.refcentro || "").trim() !== centro.refcentro) return false;
      if (fabricante?.ref && String(x.reffabricante || "").trim() !== fabricante.ref) return false;
      if (marca?.ref && String(x.refmarca || "").trim() !== marca.ref) return false;
      return true;
    });
  }, [inventarios, centro, fabricante, marca]);

  const columnsInventarios = useMemo(
    () => [
      { Header: "ID", accessor: "id", width: 70, minWidth: 60, sortType: "numPt" },
      { Header: "Nome", accessor: "nome", width: 220, minWidth: 160 },
      { Header: "Centro", accessor: "centro", width: 180, minWidth: 140 },
      { Header: "Fabricante", accessor: "fabricante", width: 260, minWidth: 180 },
      { Header: "Marca", accessor: "marca", width: 220, minWidth: 160 },
      {
        Header: "Abertura",
        accessor: "dataabertura",
        width: 150,
        minWidth: 130,
        Cell: ({ value }) => formatData(value),
      },
      {
        Header: "Encerramento",
        accessor: "dataencerramento",
        width: 150,
        minWidth: 130,
        Cell: ({ value }) => formatData(value),
      },
      {
        Header: "Aberto",
        accessor: "aberto",
        width: 90,
        minWidth: 80,
        Cell: ({ value }) => (Number(value || 0) === 1 ? "Sim" : "Não"),
      },
    ],
    []
  );

  const columnsPorContar = useMemo(
    () => [
      { Header: "Ref", accessor: "refmaterial", width: 120, minWidth: 100 },
      { Header: "Material", accessor: "material", width: 380, minWidth: 220 },
      { Header: "Unidade", accessor: "unidade", width: 100, minWidth: 80 },
      { Header: "Local Base", accessor: "local_base", width: 220, minWidth: 160 },
      { Header: "Locais Alternativos", accessor: "locais_alt", width: 320, minWidth: 180 },
    ],
    []
  );

  const columnsContados = useMemo(
    () => [
      { Header: "Ref", accessor: "refmaterial", width: 120, minWidth: 100 },
      { Header: "Material", accessor: "material", width: 360, minWidth: 220 },
      { Header: "Unidade", accessor: "unidade", width: 100, minWidth: 80 },
      {
        Header: "Qtd verificada",
        accessor: "qtd_verificada",
        width: 140,
        minWidth: 110,
        sortType: "numPt",
        filterable: false,
      },
      {
        Header: "Diferença",
        accessor: "diferenca",
        width: 120,
        minWidth: 100,
        sortType: "numPt",
        filterable: false,
      },
      { Header: "Utilizador", accessor: "utilizador", width: 180, minWidth: 140 },
      {
        Header: "Data",
        accessor: "data_registo",
        width: 150,
        minWidth: 130,
        Cell: ({ value }) => formatData(value),
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <MDBox mt={2}>
        <Card>
          <MDBox p={2} display="grid" gap={2}>
            <MDBox
              display="grid"
              gap={2}
              sx={{
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              }}
            >
              <Autocomplete
                options={centros}
                value={centro}
                onChange={(_, v) => setCentro(v)}
                getOptionLabel={(o) =>
                  o?.centro ? `${o.centro} (${o.refcentro})` : o?.refcentro ? o.refcentro : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.refcentro || "") === String(val?.refcentro || "")
                }
                renderInput={(params) => <TextField {...params} label="Centro" size="small" />}
              />

              <Autocomplete
                options={fabricantes}
                value={fabricante}
                disabled={!centro}
                onChange={(_, v) => {
                  setFabricante(v);
                  carregarMarcas(v?.ref || "");
                }}
                getOptionLabel={(o) => (o?.nome ? `${o.nome} (${o.ref})` : "")}
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.ref || "") === String(val?.ref || "")
                }
                renderInput={(params) => <TextField {...params} label="Fabricante" size="small" />}
              />

              <Autocomplete
                options={marcas}
                value={marca}
                disabled={!fabricante}
                onChange={(_, v) => setMarca(v)}
                getOptionLabel={(o) => (o?.nome ? `${o.nome} (${o.ref})` : "")}
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.ref || "") === String(val?.ref || "")
                }
                renderInput={(params) => <TextField {...params} label="Marca" size="small" />}
              />
            </MDBox>

            <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <MDButton
                color="info"
                onClick={criarInventario}
                disabled={!centro || !fabricante || !marca}
              >
                Criar Inventário
              </MDButton>

              <MDButton color="secondary" onClick={carregarInventarios}>
                Atualizar
              </MDButton>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={incluirEncerrados}
                    onChange={(e) => setIncluirEncerrados(e.target.checked)}
                    size="small"
                  />
                }
                label={<span style={{ fontSize: 12 }}>Incluir encerrados</span>}
              />
            </MDBox>
          </MDBox>
        </Card>

        <MDBox mt={2}>
          <ProjectTable
            tableKey="inventarios-lista"
            version="v1"
            title={`Inventários (${inventariosFiltrados.length})`}
            columns={columnsInventarios}
            data={inventariosFiltrados}
            loading={loading}
            minWidth={1300}
            maxHeight="48vh"
            emptyMessage="Sem inventários para os filtros selecionados."
            defaultSortBy={[{ id: "dataabertura", desc: true }]}
            rowActions={[
              {
                id: "abrir",
                icon: <OpenInNewIcon fontSize="small" />,
                tooltip: "Abrir inventário",
                color: "primary",
                onClick: abrirInventario,
              },
            ]}
          />
        </MDBox>

        {inventarioAberto && materiaisInventario && (
          <MDBox mt={2}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h6">Inventário aberto: {inventarioAberto.nome}</MDTypography>
                <MDTypography variant="caption" color="text">
                  {inventarioAberto.centro || inventarioAberto.refcentro} ·{" "}
                  {inventarioAberto.fabricante || inventarioAberto.reffabricante} ·{" "}
                  {inventarioAberto.marca || inventarioAberto.refmarca}
                </MDTypography>
              </MDBox>
            </Card>

            <MDBox mt={2}>
              <ProjectTable
                tableKey={`inventario-${inventarioAberto.id}-por-contar`}
                version="v1"
                title={`Por contar (${materiaisInventario.total_por_contar || 0})`}
                columns={columnsPorContar}
                data={materiaisInventario.por_contar || []}
                loading={loading}
                minWidth={1100}
                maxHeight="42vh"
                emptyMessage="Não existem materiais por contar."
                defaultSortBy={[{ id: "local_base", desc: false }]}
              />
            </MDBox>

            <MDBox mt={2}>
              <ProjectTable
                tableKey={`inventario-${inventarioAberto.id}-contados`}
                version="v1"
                title={`Contados (${materiaisInventario.total_contados || 0})`}
                columns={columnsContados}
                data={materiaisInventario.contados || []}
                loading={loading}
                minWidth={1200}
                maxHeight="42vh"
                emptyMessage="Ainda não existem materiais contados."
                defaultSortBy={[{ id: "data_registo", desc: true }]}
              />
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
